import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';

import { faSpinner as falSpinnerThird } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { AreaService } from '../../services/area/area.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Area } from '../../services/area/area';
import { TranslationHelperService } from '../../services/translations/translation-helper.service';
import { UntypedFormControl } from '@angular/forms';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty, unsubscribe } from '../../utils/helpers';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  standalone: false,
    selector: 'app-area-select',
    templateUrl: './area-select.component.html',
    styleUrls: ['./area-select.component.scss'] })
export class AreaSelectComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input() postcodeLabel = 'controls.postcode';
    @Input() streetLabel = 'controls.area';
    readonly icons = {
        falSpinnerThird };

    subscriptions: Subscription[] = [];
    public _selectedArea: Area;
    public _selectedCode: string;
    // @formatter:off

    @Input() get selectedArea() {
        return this._selectedArea;
    }

    @Output() selectedAreaChange = new EventEmitter();

    set selectedArea(val: Area) {
        this._selectedArea = val;
        this.selectedAreaChange.emit(this._selectedArea);
    }

    searchPostcodeForm = new UntypedFormControl('');
    searchStreetForm = new UntypedFormControl({ value: '', disabled: true });

    searchTermSubject: Subject<string | Area> = new Subject();

    results: Area[];
    results2: Area[];
    loading: boolean = false;
@ViewChild('searchPostCode') searchCode: ElementRef;
    @ViewChild('searchArea') searchArea: ElementRef;
    private lang: string;
    constructor(protected areaService: AreaService, protected translateHelperService: TranslationHelperService, private renderer: Renderer2) {
        unsubscribe(this.subscriptions);
    }

    ngAfterViewInit(): void {
        this.searchCode.nativeElement.removeAttribute('autocomplete');
        this.searchArea.nativeElement.removeAttribute('autocomplete');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.searchPostcodeForm.valueChanges
                .pipe(
                    tap((value) => {
                        this.searchStreetForm.setValue('', { emitEvent: false });
                        this.results2 = [];
                        isNullOrUndefinedOrEmpty(value) ? this.searchStreetForm.disable() : this.searchStreetForm.enable();
                    }),
                    filter((value) => !isNullOrUndefinedOrEmpty(value)),
                    debounceTime(200),
                    tap(() => (this.loading = true)),
                    switchMap((value) => {
                        return this.areaService.searchAddress(this.getSearchValue(value)).pipe(
                            finalize(() => (this.loading = false))
                        );
                    }),
                )
                .subscribe({
                    next: (res: any[]) => (this.results = res ?? []),
                    error: () => {
                        this.results = [];
                        this.loading = false;
                    }
                })
        );
        this.subscriptions.push(
            this.searchStreetForm.valueChanges
                .pipe(
                    debounceTime(250),
                    map(() => [this.searchPostcodeForm.value, this.searchStreetForm.value] as const),
                    filter(([postcode, street]) => !isNullOrUndefinedOrEmpty(postcode) && !isNullOrUndefinedOrEmpty(street)),
                    switchMap(([postcode, street]) => {
                        this.loading = true;
                        return this.areaService.searchStreet(this.getSearchValue(postcode), this.getSearchValue(street)).pipe(
                            map((areas) => areas.map(Area.fromPlain)),
                            finalize(() => (this.loading = false))
                        );
                    })
                )
                .subscribe({ next: (res) => (this.results2 = res ?? []), error: () => (this.results2 = []) })
        );

        this.translateHelperService.getLanguage().subscribe((lang) => {
            this.lang = lang;
        });
    }

    public getLocalizedName(result: any): string {
        return result?.[`name_${this.lang}`] ?? result?.[`name_fr`];
    }
    private getSearchValue(value: string | Area): string {
        return typeof value === 'string' ? value : value?.postcode ?? value?.niscode ?? value?.name ?? '';
    }

    updateActiveOption(optionId: string | null): void {
        if (optionId) {
            this.renderer.removeAttribute(this.searchCode.nativeElement, 'aria-activedescendant');
            this.renderer.setAttribute(document.getElementById(optionId), 'aria-selected', 'true');
        } else {
            this.renderer.removeAttribute(this.searchCode.nativeElement, 'aria-activedescendant');
        }
    }
    generateOptionId(option: any): string {
        return `option-${option.name || option.postcode || option.name_fr}`
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]/g, '');
    }
    onAutocompleteOpened(): void {
        this.renderer.setAttribute(this.searchCode.nativeElement, 'aria-expanded', 'true');
    }

    onAutocompleteClosed(): void {
        this.renderer.setAttribute(this.searchCode.nativeElement, 'aria-expanded', 'false');
        this.renderer.removeAttribute(this.searchCode.nativeElement, 'aria-activedescendant');
    }
    isOptionSelected(option: any): boolean {
        return this.searchCode.nativeElement.getAttribute('aria-activedescendant') === this.generateOptionId(option);
    }
    onSelect($event: MatAutocompleteSelectedEvent) {
        if (!isNaN(parseInt(this.searchPostcodeForm.value.postcode))) {
            this.searchStreetForm.enable();
        }
        this.selectedArea = $event.option.value;
    }

    onFocus(event: FocusEvent) {
        let element: any = event.target;
        element.setSelectionRange(0, element.value.length);
    }

    clear() {
        if (!isNullOrUndefined(this.selectedArea)) {
            this.selectedArea = undefined;
        }
    }

    getOptionText(option) {
        return option.postcode || option.name || option.name_fr;
    }

    ngOnDestroy(): void {
        unsubscribe(this.subscriptions);
    }
}



